'use client';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { DialogSolutionResult } from '@/features/ai-dialog';
import { TheoryMarkdown } from '@/shared/ui/markdown';

import type { ControlsTrack } from '../lib/controls-analytics';

export function DialogSolutionPanel({
	result,
	onClose,
	onRetry,
	track,
}: {
	result: DialogSolutionResult | null;
	onClose: () => void;
	onRetry: () => void;
	track: ControlsTrack;
}) {
	const close = useRef<HTMLButtonElement>(null);
	const [revealed, setRevealed] = useState(false);
	useEffect(() => {
		close.current?.focus({ preventScroll: true });
	}, []);
	return (
		<section
			aria-label="Опубликованное решение"
			className="flex h-full min-w-0 flex-1 flex-col border-l border-gray-4 bg-white"
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					event.stopPropagation();
					onClose();
				}
			}}
		>
			<header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-4 px-4">
				<h2 className="font-medium">Решение</h2>
				<button
					ref={close}
					type="button"
					onClick={onClose}
					aria-label="Закрыть решение"
					className="rounded-lg p-2 text-gray-3 hover:bg-gray-5 focus-visible:ring-2 focus-visible:ring-accent"
				>
					<X size={18} />
				</button>
			</header>
			<div className="min-h-0 flex-1 overflow-auto px-6 py-7">
				{!result ? (
					<p role="status">Загружаем решение…</p>
				) : result.status === 'error' ? (
					<div role="alert">
						<p>Не удалось загрузить решение.</p>
						<button
							type="button"
							onClick={onRetry}
							className="mt-4 rounded-lg border border-gray-4 px-4 py-2"
						>
							Повторить
						</button>
					</div>
				) : result.status === 'unavailable' ? (
					<p>Для этого языка решение недоступно.</p>
				) : !result.isSolved && !revealed ? (
					<div className="mx-auto max-w-lg text-center">
						<p>
							Постарайся решить самостоятельно и только если долго не выходит
							смотри решение, так у тебя получится лучше выучить алгоритмы
						</p>
						<button
							type="button"
							onClick={() => {
								setRevealed(true);
								track('solution', { action: 'reveal', stage: 'success' });
							}}
							className="mt-4 rounded-lg border border-gray-4 px-4 py-2 hover:bg-gray-5"
						>
							Показать решение
						</button>
					</div>
				) : (
					<div className="mx-auto max-w-[642px]">
						<TheoryMarkdown>{result.markdown}</TheoryMarkdown>
					</div>
				)}
			</div>
		</section>
	);
}
